import React from "react";
import ValidationButton from "../../utils/buttons/ValidationButton";
import CommentaryForm from "./CommentaryForm";
import './CommentaryStep.css';
import Error from "../../utils/alert/error/Error";
import {WithTranslation, withTranslation} from "react-i18next";

/**
* Interface inheriting the translation module.
* We put all the props of the component there.
**/
interface Props extends WithTranslation {
    /**
     * Function called when the user clicks on the validate button.
     * @param {string} commentary Comment entered by the user.
     */
    onValidate: (commentary: string) => void,
    /**
     * Step container class name.
     */
    className?: string,
    /**
     * Initial value of the comment.
     */
    currentCommentary?: string,
    /**
     * Specifies if the field is required and if the value entered can be null or empty.
     * @default false
     */
    required?: boolean,
}

/**
 * Component allowing to enter a comment and to validate.
 * @author Alizée Grand
 * @version 1.0
 * @creation 19/04/2022
 * @last_update 20/04/2022
 **/
class CommentaryStep extends React.Component<Props> {

    state = {
        commentary: this.props.currentCommentary !== undefined ? this.props.currentCommentary : "",
        isError: false,
    }

    render() {

        /**
         * Checks if the comment is a non-empty string.
         * @return {boolean} True if the comment is correct, otherwise false.
         */
        const isCommentCorrect = (): boolean => {
            return (
                this.state.commentary !== undefined &&
                this.state.commentary !== null &&
                this.state.commentary.length > 0
            );
        }

        /**
         * Function called when the user clicks on the validate button.
         * If the comment is correct, calls the corresponding function in the props. Otherwise, displays the appropriate errors.
         */
        const onValidate = () => {
            if(this.props.required && !isCommentCorrect()) {
                this.setState({isError: true});
            } else {
                this.props.onValidate(this.state.commentary);
            }
        }

        /**
         * Function called when the comment changes. Saves the new value in the component.
         * @param {string | null} comment New comment.
         */
        const onCommentaryChange = (comment?: string | null) => {
            this.setState({commentary: comment});
        }

        return (
            <div className={"step-main-container" + this.props.className}>
                <div className="form-container">
                    <CommentaryForm
                        currentCommentary={this.state.commentary}
                        onCommentaryChange={onCommentaryChange}
                        required={this.props.required}
                    />
                    {
                        this.state.isError ? (
                            <Error>
                                {this.props.t("steps.comment.error")}
                            </Error>
                        ) : ""
                    }
                </div>
                <div className="validation-container">
                    <ValidationButton
                        onClick={onValidate}
                    />
                </div>
            </div>
        );

    };

}

export default withTranslation()(CommentaryStep);
